import * as React from 'react';

interface Options {
  threshold?: number;
}

const documentTarget = typeof document !== 'undefined' ? document : undefined;
const documentElement = documentTarget?.documentElement;

function getTrigger(
  distanceToBottomRef: React.MutableRefObject<number | undefined>,
  options: Options,
) {
  const { threshold = 100 } = options;
  const previous = distanceToBottomRef.current;

  if (documentElement) {
    // Get distance to the bottom of the element
    // eslint-disable-next-line no-param-reassign
    distanceToBottomRef.current =
      documentElement.offsetHeight -
      documentElement.scrollTop -
      documentElement.clientHeight;
  }

  if (distanceToBottomRef.current !== undefined) {
    if (
      distanceToBottomRef.current < threshold ||
      previous === undefined ||
      (previous !== undefined && distanceToBottomRef.current > previous)
    ) {
      return true;
    }
  }

  return false;
}

// based on @material-ui's useScrollTrigger https://github.com/mui-org/material-ui/blob/256fa6e6a14f46025d227193877a4223149ee018/packages/material-ui/src/useScrollTrigger/useScrollTrigger.js
export default function useBottomScrollTrigger(options: Options = {}): boolean {
  const distanceToBottomRef = React.useRef<number>();
  const [trigger, setTrigger] = React.useState(true);

  React.useEffect(() => {
    if (!documentTarget) {
      return undefined;
    }

    const handleScroll = () => {
      setTrigger(getTrigger(distanceToBottomRef, options));
    };

    handleScroll(); // Re-evaluate trigger when dependencies change
    documentTarget.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      documentTarget.removeEventListener('scroll', handleScroll);
    };
  }, [options.threshold]);

  return trigger;
}
